/*
 * Similar items.
 *
 * A sold out size on the product page opens a sheet with products that are in stock
 * in that size: same product type, same kind of pattern or a similar colour first,
 * each one addable to the bag in that size.
 *
 * Results come from the storefront filters the collection pages already use,
 * requested through the Section Rendering API (sections/similar-items.liquid):
 *
 *   /collections/all?section_id=similar-items&sort_by=best-selling
 *     &filter.v.availability=1&filter.v.option.size=M&filter.v.option.size=38
 *     &filter.p.product_type=Blouses&filter.p.m.custom.color_filter_name=Blue
 *
 * Shopify applies filter.v.* per variant, so size plus availability means "this exact
 * size is in stock", not "this product has stock somewhere".
 *
 * Repeating a filter is an OR, different filters are an AND, and the response has no
 * relevance order. "Same pattern or similar colour" is therefore two requests, sent
 * together, and "anything else of this type" a third one, only when the first two did
 * not bring enough. The merged set is then ranked on what every item actually shares
 * with the product on the page.
 */
if (!customElements.get('similar-items')) {
  class SimilarItems extends HTMLElement {
    constructor() {
      super();
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      /* Keyed by size, so opening the same size again is instant. */
      this.cache = new Map();
    }

    connectedCallback() {
      /* The product section this sheet belongs to: its triggers, size selector and notify button. */
      this.section = this.closest('.shopify-section') || document;
      this.panel = this.querySelector('[data-similar-items-panel]');
      this.overlay = this.querySelector('.similar-items__overlay');
      this.body = this.querySelector('[data-similar-items-body]');
      this.count = this.querySelector('[data-similar-items-count]');
      this.status = this.querySelector('[data-similar-items-status]');
      this.sizeLabel = this.querySelector('[data-similar-items-size-label]');
      this.notifyButton = this.querySelector('[data-similar-items-notify]');
      this.limit = parseInt(this.dataset.limit, 10) || 12;

      try {
        this.data = JSON.parse(this.querySelector('[data-similar-items-data]').textContent);
      } catch (error) {
        this.data = { sizes: [], variants: [] };
      }

      this.query = {
        handle: this.dataset.handle,
        type: this.dataset.productType,
        colour: this.split(this.dataset.colour),
        colours: this.split(this.dataset.colours),
        print: this.split(this.dataset.print),
        prints: this.split(this.dataset.prints),
      };

      /*
       * Captured on the way down. The "View similar" in the mobile size list sits in a
       * row the theme turns into a quick add, and that row must never see the click.
       */
      document.addEventListener('click', this.onDocumentClick, true);
      document.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick, true);
      document.removeEventListener('keydown', this.onKeydown);
      this.controller?.abort();
      document.body.classList.remove('open-similar-items');
    }

    split(value) {
      return (value || '')
        .split('|')
        .map((part) => part.trim())
        .filter(Boolean);
    }

    /* Clicks ------------------------------------------------------------- */

    onDocumentClick(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const opener = target.closest('[data-similar-items-open]');
      if (opener && this.section.contains(opener)) {
        event.preventDefault();
        event.stopPropagation();
        this.open(opener.dataset.size || this.selectedSize(), opener);
        return;
      }

      if (!this.contains(target)) return;

      if (target.closest('[data-similar-items-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      const sizeButton = target.closest('[data-similar-items-size]');
      if (sizeButton) {
        event.preventDefault();
        this.setSize(sizeButton.dataset.similarItemsSize);
        return;
      }

      if (target.closest('[data-similar-items-notify]')) {
        event.preventDefault();
        this.notify();
        return;
      }

      if (target.closest('[data-similar-items-retry]')) {
        event.preventDefault();
        this.cache.delete(this.size || '*');
        this.load(this.size);
        return;
      }

      const add = target.closest('[data-similar-item-add]');
      if (add) {
        event.preventDefault();
        this.addToCart(add);
      }
    }

    onKeydown(event) {
      if (!document.body.classList.contains('open-similar-items')) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      /* Keeps the focus inside the sheet while it is open. */
      if (event.key !== 'Tab') return;
      const focusable = Array.from(this.panel.querySelectorAll('a[href], button:not([disabled])')).filter(
        (element) => element.offsetParent !== null && element.getAttribute('tabindex') !== '-1'
      );
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* Sizes -------------------------------------------------------------- */

    /* The size selector of this product, never one in a popup showing another product. */
    sizeInputs() {
      const radios = this.section.querySelector('.section-product variant-radios');
      if (!radios) return [];
      return Array.from(radios.querySelectorAll('.option-wrap')).filter(
        (wrap) => (wrap.dataset.name || '').toLowerCase() === 'size'
      ).flatMap((wrap) => Array.from(wrap.querySelectorAll('input[type="radio"]')));
    }

    /*
     * The size picked on the page: the checked size in the size selector, or else the
     * variant in the url, which the jeans popup keeps up to date as well. Nothing
     * picked yet opens the sheet on its own size list.
     */
    selectedSize() {
      const checked = this.sizeInputs().find((input) => input.checked);
      if (checked) return checked.value;

      const id = new URLSearchParams(window.location.search).get('variant');
      const variant = id && this.data.variants.find((entry) => String(entry.id) === id);
      return variant && variant.size ? variant.size : '';
    }

    sizeEntry(size) {
      return this.data.sizes.find((entry) => entry.value === size);
    }

    /* The size itself first, then the sizes that fit the same (M, then 38). */
    sizeMatch(size) {
      if (!size) return [];
      const entry = this.sizeEntry(size);
      return entry && entry.match.length ? entry.match : [size];
    }

    hasSizes() {
      return this.data.sizes.length > 0;
    }

    /* Sheet -------------------------------------------------------------- */

    open(size, opener) {
      this.opener = opener || document.activeElement;
      /* The mobile size list and the jeans popup make way. */
      document.body.classList.remove('open-mb-size', 'open-size');

      clearTimeout(this.hideTimer);
      this.overlay.hidden = false;
      this.panel.hidden = false;
      /* A style read in between, so the sheet has a closed state to slide in from. */
      this.panel.getBoundingClientRect();
      document.body.classList.add('open-similar-items');
      this.panel.focus({ preventScroll: true });

      this.setSize(size || '');
    }

    close({ restoreFocus = true } = {}) {
      if (!document.body.classList.contains('open-similar-items')) return;
      document.body.classList.remove('open-similar-items');
      this.controller?.abort();

      /* Out of the tab order once it has slid away. */
      clearTimeout(this.hideTimer);
      this.hideTimer = setTimeout(() => {
        this.panel.hidden = true;
        this.overlay.hidden = true;
      }, 900);

      if (restoreFocus && this.opener && document.contains(this.opener)) {
        this.opener.focus({ preventScroll: true });
      }
    }

    setSize(size) {
      this.size = size;

      this.querySelectorAll('[data-similar-items-size]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.similarItemsSize === size));
      });
      if (this.sizeLabel) {
        this.sizeLabel.textContent = size ? `${this.dataset.labelSize} ${size}` : '';
      }

      /*
       * Signing up only makes sense for a size that is sold out on this product. A
       * product without sizes only gets here when it is sold out altogether.
       */
      const entry = this.sizeEntry(size);
      this.notifyButton.hidden = this.hasSizes() ? !(entry && !entry.available) : false;

      if (this.hasSizes() && !size) {
        this.controller?.abort();
        this.count.textContent = '';
        this.renderMessage(this.dataset.labelChoose);
        return;
      }

      this.load(size);
    }

    /* Results ------------------------------------------------------------ */

    async load(size) {
      const key = size || '*';
      this.controller?.abort();

      if (this.cache.has(key)) {
        this.render(this.cache.get(key), size);
        return;
      }

      const controller = new AbortController();
      this.controller = controller;
      this.renderLoading();

      try {
        const items = await this.fetchItems(size, controller.signal);
        const ranked = this.rank(items).slice(0, this.limit);
        this.cache.set(key, ranked);
        if (this.controller !== controller) return;
        this.render(ranked, size);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.renderError();
      }
    }

    async fetchItems(size, signal) {
      const sizes = this.sizeMatch(size);
      const seen = new Set([this.query.handle]);
      const items = [];
      let requests = 0;
      let failures = 0;

      const collect = (found) => {
        requests += 1;
        if (!found) {
          failures += 1;
          return;
        }
        found.forEach((item) => {
          if (seen.has(item.dataset.handle)) return;
          seen.add(item.dataset.handle);
          items.push(item);
        });
      };

      /* Pattern and colour are equally good matches, so they are asked for together. */
      const narrow = [this.buildUrl('print', sizes), this.buildUrl('colour', sizes)].filter(Boolean);
      const found = await Promise.all(narrow.map((url) => this.fetchTier(url, signal)));
      found.forEach(collect);

      if (items.length < this.limit && this.dataset.fill === 'true') {
        collect(await this.fetchTier(this.buildUrl('type', sizes), signal));
      }

      if (requests && failures === requests) throw new Error('Similar items could not be loaded');
      return items;
    }

    /*
     * Every tier has size, availability and the product type. The type is never
     * relaxed: a blouse only ever suggests blouses.
     */
    buildUrl(tier, sizes) {
      const params = new URLSearchParams();
      params.append('section_id', this.dataset.sectionId);
      params.append('sort_by', 'best-selling');
      params.append('filter.v.availability', '1');
      params.append(this.dataset.typeParam, this.query.type);
      sizes.forEach((size) => params.append(this.dataset.sizeParam, size));

      if (tier === 'print') {
        if (!this.query.prints.length) return null;
        this.query.prints.forEach((value) => params.append(this.dataset.printParam, value));
      }

      if (tier === 'colour') {
        if (!this.query.colours.length) return null;
        this.query.colours.forEach((value) => params.append(this.dataset.colourParam, value));
      }

      return `${this.dataset.collectionUrl}?${params.toString()}`;
    }

    /* Null when the request failed, so one bad tier does not sink the others. */
    async fetchTier(url, signal) {
      try {
        const response = await fetch(url, { signal });
        if (!response.ok) return null;
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        return Array.from(html.querySelectorAll('[data-similar-item]'));
      } catch (error) {
        if (error.name === 'AbortError') throw error;
        return null;
      }
    }

    /*
     * Same print, then same colour, then the same kind of pattern, then a similar
     * colour, with the order the requests brought them in underneath: best selling,
     * and the pattern and colour matches before the rest of the type.
     */
    rank(items) {
      const lower = (values) => new Set(values.map((value) => value.toLowerCase()));
      const print = lower(this.query.print);
      const prints = lower(this.query.prints);
      const colour = lower(this.query.colour);
      const colours = lower(this.query.colours);
      const shares = (values, set) => values.some((value) => set.has(value));

      return items
        .map((item, index) => {
          const itemPrint = this.split(item.dataset.print).map((value) => value.toLowerCase());
          const itemColour = this.split(item.dataset.colour).map((value) => value.toLowerCase());
          let score = 0;
          if (shares(itemPrint, print)) score += 4;
          else if (shares(itemPrint, prints)) score += 2;
          if (shares(itemColour, colour)) score += 3;
          else if (shares(itemColour, colours)) score += 1;
          return { item, index, score };
        })
        .sort((a, b) => b.score - a.score || a.index - b.index)
        .map((entry) => entry.item);
    }

    /* The variant of an item in the size the sheet is showing. */
    variantFor(item, size) {
      let variants = [];
      try {
        variants = JSON.parse(item.querySelector('[data-similar-item-variants]').textContent);
      } catch (error) {
        return null;
      }

      const available = variants.filter((variant) => variant.available);
      if (!size) return available.length === 1 ? available[0] : null;

      for (const wanted of this.sizeMatch(size)) {
        const match = available.find((variant) => (variant.size || '').toLowerCase() === wanted.toLowerCase());
        if (match) return match;
      }
      return null;
    }

    /* Rendering ---------------------------------------------------------- */

    render(items, size) {
      this.body.removeAttribute('aria-busy');
      const countText =
        items.length === 1
          ? this.dataset.labelCountOne
          : this.dataset.labelCountOther.replace('[count]', items.length);

      if (!items.length) {
        this.count.textContent = '';
        this.renderMessage(size ? this.dataset.labelEmpty.replace('[size]', size) : this.dataset.labelEmptyNoSize);
        return;
      }

      this.count.textContent = countText;
      this.status.textContent = size ? `${countText}, ${this.dataset.labelSize} ${size}` : countText;

      const list = document.createElement('ul');
      list.className = 'similar-items__results';
      /* Cloned, so the cached results stay as they came in. */
      items.forEach((item) => list.appendChild(this.prepare(item.cloneNode(true), size)));

      this.body.replaceChildren(list);
      this.body.scrollTop = 0;
    }

    prepare(item, size) {
      const variant = this.variantFor(item, size);
      const add = item.querySelector('[data-similar-item-add]');

      if (variant && add) {
        add.dataset.variantId = variant.id;
        add.hidden = false;
        add.setAttribute(
          'aria-label',
          this.dataset.labelAdd.replace('[title]', add.dataset.title || '').replace('[size]', variant.size || size)
        );

        /* The product page opens on the size the customer was looking for. */
        item.querySelectorAll('[data-similar-item-link]').forEach((link) => {
          const url = new URL(link.getAttribute('href'), window.location.origin);
          url.searchParams.set('variant', variant.id);
          link.setAttribute('href', `${url.pathname}${url.search}`);
        });
      }

      /* Fades in like the collection grid. */
      item.querySelectorAll('.similar-card__media img').forEach((image) => {
        if (image.complete) {
          image.classList.add('loaded');
        } else {
          image.addEventListener('load', () => image.classList.add('loaded'), { once: true });
        }
      });

      return item;
    }

    /*
     * As many placeholders as there will be results (two rows of six at the default of
     * twelve), so the sheet does not change height when they come in.
     */
    renderLoading() {
      this.body.setAttribute('aria-busy', 'true');
      this.count.textContent = '';

      const list = document.createElement('ul');
      list.className = 'similar-items__results';
      list.setAttribute('aria-hidden', 'true');
      for (let index = 0; index < this.limit; index += 1) {
        const item = document.createElement('li');
        item.className = 'similar-items__item similar-items__item--skeleton';
        item.innerHTML =
          '<span class="similar-card__media"></span><span class="similar-card__line"></span><span class="similar-card__line similar-card__line--short"></span>';
        list.appendChild(item);
      }
      this.body.replaceChildren(list);
    }

    renderMessage(text, { retry = false } = {}) {
      this.body.removeAttribute('aria-busy');
      const message = document.createElement('div');
      message.className = 'similar-items__message';

      const paragraph = document.createElement('p');
      paragraph.textContent = text;
      message.appendChild(paragraph);

      if (retry) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'similar-items__retry';
        button.dataset.similarItemsRetry = '';
        button.textContent = this.dataset.labelRetry;
        message.appendChild(button);
      }

      this.body.replaceChildren(message);
      this.status.textContent = text;
    }

    renderError() {
      this.count.textContent = '';
      this.renderMessage(this.dataset.labelError, { retry: true });
    }

    /* Bag ---------------------------------------------------------------- */

    async addToCart(button) {
      if (button.classList.contains('is-loading') || !button.dataset.variantId) return;

      const error = button.closest('[data-similar-item]')?.querySelector('[data-similar-item-error]');
      if (error) error.hidden = true;
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ items: [{ id: Number(button.dataset.variantId), quantity: 1 }] }),
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || this.dataset.labelError);
        }

        button.classList.add('is-added');
        button.setAttribute('aria-label', this.dataset.labelAdded);
        this.close({ restoreFocus: false });
        this.afterAdd(data.items ? data.items[0] : data);
      } catch (addError) {
        if (error) {
          error.textContent = addError.message;
          error.hidden = false;
        }
      } finally {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
      }
    }

    /*
     * The same hand-over as the add to bag button on the page (product.js): the cart
     * notification, the drawer refresh and the multiple sizes check.
     */
    afterAdd(item) {
      document.dispatchEvent(new CustomEvent('cart:added', { detail: item }));

      if (typeof window.load_cart !== 'function') {
        window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
        return;
      }

      if (typeof window.cartNotification === 'function') {
        window.cartNotification(item.id);
      } else {
        document.body.classList.add('open-drawer');
      }
      window.load_cart();
      if (typeof window.checkMultipleSizesAndTriggerPopup === 'function') {
        window.checkMultipleSizesAndTriggerPopup();
      }
    }

    /*
     * Signing up goes through the page's own notify me button, which Klaviyo is bound
     * to. The size is picked in the size selector first, so the sign up is for the
     * size the customer was looking at.
     */
    notify() {
      const size = this.size;
      this.close({ restoreFocus: false });

      const input = size && this.sizeInputs().find((radio) => radio.value === size);
      if (input && !input.checked) input.click();

      const trigger =
        this.section.querySelector('.section-product .choose-cart .klaviyo-bis-trigger') ||
        this.section.querySelector('.section-product .klaviyo-bis-trigger');
      trigger?.click();
    }
  }

  customElements.define('similar-items', SimilarItems);
}
